import { useState, memo, useMemo, useCallback } from 'react';
import { chord as d3Chord, ribbonArrow } from 'd3-chord';
import { arc as d3Arc } from 'd3-shape';
import { descending } from 'd3-array';
import { CGIAR_COLORS, DELIVERY_TYPE_COLORS } from '../shared/constants';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { usePartnershipData } from './usePartnershipData.jsx';

const SIZE = 600;
const CX = SIZE / 2;
const CY = SIZE / 2;
const OUTER_R = 255;
const INNER_R = 230;
const LABEL_R = OUTER_R + 14;

export const PartnerProgramNetwork = memo(function PartnerProgramNetwork({
  edges: edgesProp,
  programs: programsProp,
  partners: partnersProp,
}) {
  const { networkData, localFilteredResults } = usePartnershipData();
  const edges = edgesProp ?? networkData.edges;
  const programs = programsProp ?? networkData.programNodes;
  const partners = partnersProp ?? networkData.partnerNodes;

  const [hoveredNode, setHoveredNode] = useState(null);
  const [hoveredChord, setHoveredChord] = useState(null);
  const [tip, setTip] = useState(null);
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });

  // Export data for ChartWrapper
  const chartData = useMemo(() => {
    if (!edges || edges.length === 0) return [];
    return edges.map(e => ({
      partner: e.partner,
      program: e.program,
      result_count: e.count,
    }));
  }, [edges]);

  // Build the chord layout data
  const chordLayout = useMemo(() => {
    if (!edges?.length || !programs?.length || !partners?.length) return null;

    // Nodes: programs first, then partners
    const nodes = [
      ...programs.map(p => ({
        id: p.code,
        label: p.code,
        fullName: p.name,
        totalResults: p.totalResults,
        type: 'program',
      })),
      ...partners.map(p => ({
        id: p.fullName || p.acronym,
        label: p.acronym.length > 10 ? p.acronym.substring(0, 9) + '\u2026' : p.acronym,
        fullName: p.fullName,
        totalResults: p.totalResults,
        type: 'partner',
        primaryType: p.deliveryTypes?.[0] || 'Other',
      })),
    ];

    const n = nodes.length;
    const indexMap = {};
    nodes.forEach((node, i) => { indexMap[node.id] = i; });

    // Build symmetric matrix
    const matrix = Array.from({ length: n }, () => new Float64Array(n));
    for (const e of edges) {
      const pi = indexMap[e.program];
      const pai = indexMap[e.partner];
      if (pi !== undefined && pai !== undefined) {
        matrix[pi][pai] = e.count;
        matrix[pai][pi] = e.count;
      }
    }

    // Compute chord layout
    const layout = d3Chord()
      .padAngle(0.03)
      .sortSubgroups(descending)
      .sortChords(descending)(matrix);

    return { nodes, layout, indexMap, programCount: programs.length };
  }, [edges, programs, partners]);

  // Arc and ribbon generators
  const arcGen = useMemo(() =>
    d3Arc().innerRadius(INNER_R).outerRadius(OUTER_R),
  []);

  const ribbonGen = useMemo(() =>
    ribbonArrow().radius(INNER_R - 2).headRadius(6),
  []);

  // Node color lookup
  const nodeColor = useCallback((idx, nodes) => {
    const node = nodes[idx];
    if (!node) return '#94a3b8';
    if (node.type === 'program') {
      return CGIAR_COLORS[idx % CGIAR_COLORS.length];
    }
    return DELIVERY_TYPE_COLORS[node.primaryType] || DELIVERY_TYPE_COLORS['Other'];
  }, []);

  // Highlight logic
  const isArcHighlighted = useCallback((groupIdx) => {
    if (hoveredChord !== null) {
      return groupIdx === hoveredChord.source.index || groupIdx === hoveredChord.target.index;
    }
    if (hoveredNode !== null) return groupIdx === hoveredNode;
    return true; // nothing hovered, all highlighted
  }, [hoveredNode, hoveredChord]);

  const isRibbonHighlighted = useCallback((ch) => {
    if (hoveredChord !== null) {
      return ch.source.index === hoveredChord.source.index &&
             ch.target.index === hoveredChord.target.index;
    }
    if (hoveredNode !== null) {
      return ch.source.index === hoveredNode || ch.target.index === hoveredNode;
    }
    return true;
  }, [hoveredNode, hoveredChord]);

  if (!chordLayout) return (
    <ChartWrapper title="Partner-Program Network">
      <NoChartData message="No partner network data available" detail="This chord diagram shows the network of partner-program relationships. Not enough partner data with program associations was found to generate the network." />
    </ChartWrapper>
  );

  const { nodes, layout, programCount } = chordLayout;
  const { groups, ...chords } = layout;
  // layout itself is the array of chords, groups is the groups
  const chordArray = Array.from(layout);

  return (
    <ChartWrapper title="Partner-Program Network" info="Chord diagram showing the network of connections between external partners and CGIAR programs. Thicker ribbons indicate more shared results." chartData={chartData} sourceResults={localFilteredResults} exportFilename="partner-program-network">
      <div className="flex justify-center">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="w-full"
          style={{ maxWidth: SIZE, height: 'auto' }}
        >
          <defs>
            {/* Gradient for each ribbon: source color → target color */}
            {chordArray.map((ch, i) => {
              const srcColor = nodeColor(ch.source.index, nodes);
              const tgtColor = nodeColor(ch.target.index, nodes);
              // Compute midpoints of source and target arcs for gradient direction
              const srcAngle = (ch.source.startAngle + ch.source.endAngle) / 2 - Math.PI / 2;
              const tgtAngle = (ch.target.startAngle + ch.target.endAngle) / 2 - Math.PI / 2;
              return (
                <linearGradient
                  key={i}
                  id={`chord-grad-${i}`}
                  gradientUnits="userSpaceOnUse"
                  x1={CX + INNER_R * 0.5 * Math.cos(srcAngle)}
                  y1={CY + INNER_R * 0.5 * Math.sin(srcAngle)}
                  x2={CX + INNER_R * 0.5 * Math.cos(tgtAngle)}
                  y2={CY + INNER_R * 0.5 * Math.sin(tgtAngle)}
                >
                  <stop offset="0%" stopColor={srcColor} />
                  <stop offset="100%" stopColor={tgtColor} />
                </linearGradient>
              );
            })}
          </defs>

          <g transform={`translate(${CX},${CY})`}>
            {/* Ribbons */}
            {chordArray.map((ch, i) => {
              const highlighted = isRibbonHighlighted(ch);
              const srcNode = nodes[ch.source.index];
              const tgtNode = nodes[ch.target.index];
              const count = ch.source.value;
              return (
                <path
                  key={`ribbon-${i}`}
                  d={ribbonGen(ch)}
                  fill={`url(#chord-grad-${i})`}
                  opacity={highlighted ? 0.55 : 0.04}
                  style={{ transition: 'opacity 0.3s ease', cursor: 'pointer' }}
                  onMouseEnter={(e) => {
                    setHoveredChord(ch);
                    setTip({ source: srcNode?.fullName || srcNode?.label, target: tgtNode?.fullName || tgtNode?.label, count, sourceColor: nodeColor(ch.source.index, nodes), targetColor: nodeColor(ch.target.index, nodes) });
                    setTipPos({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseMove={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => { setHoveredChord(null); setTip(null); }}
                />
              );
            })}

            {/* Arcs */}
            {groups.map((group, i) => {
              const highlighted = isArcHighlighted(i);
              const color = nodeColor(i, nodes);
              const node = nodes[i];
              // Compute label position
              const midAngle = (group.startAngle + group.endAngle) / 2;
              const lx = LABEL_R * Math.sin(midAngle);
              const ly = -LABEL_R * Math.cos(midAngle);
              const rotation = (midAngle * 180) / Math.PI;
              const flip = midAngle > Math.PI;
              return (
                <g key={`arc-${i}`}>
                  <path
                    d={arcGen(group)}
                    fill={color}
                    opacity={highlighted ? 1 : 0.15}
                    stroke="#fff"
                    strokeWidth={1}
                    style={{ transition: 'opacity 0.3s ease', cursor: 'pointer' }}
                    onMouseEnter={(e) => { setHoveredNode(i); setHoveredChord(null); setTip({ name: node?.fullName || node?.label, totalResults: node?.totalResults, nodeType: node?.type === 'program' ? 'Program' : node?.primaryType || 'Partner', color }); setTipPos({ x: e.clientX, y: e.clientY }); }}
                    onMouseMove={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => { setHoveredNode(null); setTip(null); }}
                  />
                  <text
                    x={lx}
                    y={ly}
                    dy="0.35em"
                    textAnchor={flip ? 'end' : 'start'}
                    transform={`rotate(${rotation - 90},${lx},${ly})${flip ? ` rotate(180,${lx},${ly})` : ''}`}
                    fontSize={node?.type === 'program' ? 9 : 7.5}
                    fontWeight={node?.type === 'program' ? 700 : 500}
                    fill={highlighted ? '#1f2937' : '#d1d5db'}
                    style={{ transition: 'fill 0.3s ease', cursor: 'pointer' }}
                    onMouseEnter={(e) => { setHoveredNode(i); setHoveredChord(null); setTip({ name: node?.fullName || node?.label, totalResults: node?.totalResults, nodeType: node?.type === 'program' ? 'Program' : node?.primaryType || 'Partner', color }); setTipPos({ x: e.clientX, y: e.clientY }); }}
                    onMouseMove={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => { setHoveredNode(null); setTip(null); }}
                  >
                    {node?.label}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>
        {tip && (
          <div className="fixed z-50 px-3 py-2 bg-white text-gray-800 text-xs rounded-lg shadow-lg border border-gray-200 pointer-events-none"
               style={{ left: tipPos.x + 12, top: tipPos.y - 28 }}>
            {tip.totalResults !== undefined ? (
              <>
                <div className="font-semibold mb-1">{tip.name}</div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: tip.color }} />
                  Results: {tip.totalResults}
                </div>
                <div className="text-gray-500 mt-1 border-t border-gray-200 pt-1">{tip.nodeType}</div>
              </>
            ) : (
              <>
                <div className="font-semibold mb-1">{tip.source} ↔ {tip.target}</div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: tip.sourceColor }} />
                  <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: tip.targetColor }} />
                  Shared results: {tip.count}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center justify-center gap-4 text-xs text-gray-600">
        <span className="font-medium text-gray-700">Programs:</span>
        {programs.map((p, i) => (
          <div key={p.code} className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: CGIAR_COLORS[i % CGIAR_COLORS.length] }} />
            <span>{p.code}</span>
          </div>
        ))}
        <span className="text-gray-300">|</span>
        <span className="font-medium text-gray-700">Partner type:</span>
        {Object.entries(DELIVERY_TYPE_COLORS).map(([type, color]) => (
          <div key={type} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
            <span>{type}</span>
          </div>
        ))}
      </div>
    </ChartWrapper>
  );
});
